import { useEffect, useState, useCallback } from "react";
import type { DisciplineSlug } from "@jumpbean/shared";
import { StarDisplay } from "./StarDisplay";
import { Confetti } from "./Confetti";

export interface LevelCompleteSummaryProps {
  /** Level number completed (1-5) */
  levelNumber: number;
  /** Level name */
  levelName: string;
  /** Discipline for theming */
  discipline: DisciplineSlug;
  /** Character name for the discipline */
  characterName: string;
  /** Character image URL */
  characterImageUrl?: string;
  /** Total stars earned in this level */
  starsEarned: number;
  /** Maximum possible stars for the level */
  maxStars: number;
  /** Badge name if earned */
  badgeName?: string;
  /** Badge image URL if earned */
  badgeImageUrl?: string;
  /** Called when continue button is pressed */
  onContinue: () => void;
  /** Called when replay level is pressed */
  onReplay?: () => void;
  /** Whether to show the replay option */
  showReplay?: boolean;
}

/**
 * Discipline theme colors
 */
const DISCIPLINE_THEMES: Record<DisciplineSlug, {
  bgGradient: string;
  accentColor: string;
  textColor: string;
  buttonBg: string;
  buttonBorder: string;
  buttonShadow: string;
}> = {
  math: {
    bgGradient: "from-luna-light via-luna to-magic-200",
    accentColor: "text-magic-700",
    textColor: "text-magic-800",
    buttonBg: "bg-gradient-to-br from-luna-light to-luna",
    buttonBorder: "border-luna-dark",
    buttonShadow: "shadow-luna-dark/30",
  },
  spelling: {
    bgGradient: "from-petal-light via-meadow-200 to-meadow-100",
    accentColor: "text-meadow-700",
    textColor: "text-meadow-800",
    buttonBg: "bg-gradient-to-br from-petal-light to-petal",
    buttonBorder: "border-petal-dark",
    buttonShadow: "shadow-petal-dark/30",
  },
  patterns: {
    bgGradient: "from-prism-purple via-prism-blue to-prism-green",
    accentColor: "text-magic-600",
    textColor: "text-neutral-800",
    buttonBg: "bg-gradient-rainbow",
    buttonBorder: "border-prism-purple",
    buttonShadow: "shadow-prism-purple/30",
  },
  typing: {
    bgGradient: "from-whiskers-light via-sky-200 to-sky-100",
    accentColor: "text-sky-700",
    textColor: "text-sky-800",
    buttonBg: "bg-gradient-to-br from-whiskers-light to-whiskers",
    buttonBorder: "border-whiskers-dark",
    buttonShadow: "shadow-whiskers-dark/30",
  },
  programming: {
    bgGradient: "from-pixel-light via-encourage-light to-sunshine-100",
    accentColor: "text-pixel-dark",
    textColor: "text-pixel-dark",
    buttonBg: "bg-gradient-to-br from-pixel-light to-pixel",
    buttonBorder: "border-pixel-dark",
    buttonShadow: "shadow-pixel-dark/30",
  },
};

/**
 * LevelCompleteSummary - Celebratory summary shown after completing a level
 *
 * Features:
 * - Animated star reveal sequence
 * - Total stars earned display
 * - Badge reveal if earned
 * - Character celebration
 * - Confetti effects
 */
export function LevelCompleteSummary({
  levelNumber,
  levelName,
  discipline,
  characterName,
  characterImageUrl,
  starsEarned,
  maxStars,
  badgeName,
  badgeImageUrl,
  onContinue,
  onReplay,
  showReplay = false,
}: LevelCompleteSummaryProps) {
  const [phase, setPhase] = useState<"entering" | "stars" | "badge" | "complete">("entering");
  const [showConfetti, setShowConfetti] = useState(false);

  const theme = DISCIPLINE_THEMES[discipline];

  // Calculate star rating (1-3) for display
  const starRating = Math.min(3, Math.max(0, Math.round((starsEarned / maxStars) * 3))) as 0 | 1 | 2 | 3;

  // Animation sequence
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];

    // Phase 1: Enter animation
    timers.push(setTimeout(() => {
      setPhase("stars");
      setShowConfetti(true);
    }, 500));

    // Phase 2: Show badge (if earned)
    if (badgeName) {
      timers.push(setTimeout(() => {
        setPhase("badge");
      }, 2500));
    }

    // Phase 3: Complete
    timers.push(setTimeout(() => {
      setPhase("complete");
    }, badgeName ? 4000 : 2500));

    return () => timers.forEach(clearTimeout);
  }, [badgeName]);

  const handleStarsComplete = useCallback(() => {
    // Stars animation done
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className={`
          absolute inset-0
          bg-gradient-to-b ${theme.bgGradient}
          opacity-95
        `}
      />

      {/* Floating decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 8 }).map((_, i) => (
          <FloatingShape key={i} index={i} discipline={discipline} />
        ))}
      </div>

      {/* Confetti */}
      <Confetti isActive={showConfetti} count={100} duration={4000} />

      {/* Main content */}
      <div
        className={`
          relative z-10
          flex flex-col items-center
          max-w-lg w-full
          bg-white/90 backdrop-blur-sm
          rounded-3xl
          shadow-2xl
          p-8 md:p-12
          transition-all duration-500
          ${phase === "entering" ? "scale-90 opacity-0" : "scale-100 opacity-100"}
        `}
      >
        {/* Character celebration */}
        {characterImageUrl && (
          <div
            className={`
              absolute -top-16 left-1/2 -translate-x-1/2
              w-32 h-32
              animate-float
            `}
          >
            <img
              src={characterImageUrl}
              alt={characterName}
              className="w-full h-full object-contain drop-shadow-lg"
            />
          </div>
        )}

        {/* Level Complete Header */}
        <div className="text-center mt-8">
          <h2
            className={`
              font-display font-extrabold
              text-display-md md:text-display-lg
              ${theme.accentColor}
              animate-celebrate
            `}
          >
            Level Complete!
          </h2>
          <p className={`font-body font-semibold text-body-lg ${theme.textColor} mt-2`}>
            {levelName}
          </p>
        </div>

        {/* Stars Section */}
        <div className="mt-8 flex flex-col items-center">
          <StarDisplay
            stars={starRating}
            size="xl"
            animate={phase !== "entering"}
            animationDelay={300}
            showSparkles
            onAnimationComplete={handleStarsComplete}
          />

          {/* Star count */}
          <div className="mt-4 flex items-center gap-2">
            <span className="font-display font-bold text-display-sm text-sunshine-600">
              {starsEarned}
            </span>
            <span className="font-body text-body-lg text-neutral-600">
              / {maxStars} stars earned
            </span>
          </div>
        </div>

        {/* Badge Section */}
        {badgeName && (
          <div
            className={`
              mt-8 flex flex-col items-center
              transition-all duration-500
              ${phase === "badge" || phase === "complete" ? "opacity-100 scale-100" : "opacity-0 scale-75"}
            `}
          >
            <div className="relative">
              {/* Badge glow */}
              <div className="absolute inset-0 rounded-full bg-sunshine-400/30 blur-xl scale-150" />

              {/* Badge image or placeholder */}
              <div
                className={`
                  relative
                  w-24 h-24
                  rounded-full
                  bg-gradient-to-br from-sunshine-300 via-sunshine-400 to-sunshine-500
                  border-4 border-sunshine-600
                  flex items-center justify-center
                  shadow-lg
                  ${phase === "badge" ? "animate-star-pop" : ""}
                `}
              >
                {badgeImageUrl ? (
                  <img
                    src={badgeImageUrl}
                    alt={badgeName}
                    className="w-16 h-16 object-contain"
                  />
                ) : (
                  <span className="text-4xl">🏆</span>
                )}
              </div>
            </div>

            <p className="mt-3 font-display font-bold text-body-xl text-sunshine-700">
              Badge Earned!
            </p>
            <p className="font-body font-semibold text-body-md text-neutral-600">
              {badgeName}
            </p>
          </div>
        )}

        {/* Actions */}
        <div
          className={`
            mt-10 flex flex-col sm:flex-row gap-4 w-full
            transition-all duration-300
            ${phase === "complete" ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}
          `}
        >
          {showReplay && onReplay && (
            <button
              onClick={onReplay}
              className={`
                flex-1
                min-h-touch
                px-8
                font-display font-bold text-body-lg
                bg-white
                text-neutral-700
                border-3 border-neutral-300
                rounded-full
                shadow-md
                transition-all duration-200
                hover:scale-105 hover:shadow-lg
                active:scale-95
              `}
            >
              Replay Level
            </button>
          )}

          <button
            onClick={onContinue}
            className={`
              flex-1
              min-h-touch
              px-8
              font-display font-bold text-body-lg
              ${theme.buttonBg}
              text-white
              border-3 ${theme.buttonBorder}
              rounded-full
              shadow-lg ${theme.buttonShadow}
              transition-all duration-200
              hover:scale-105 hover:shadow-xl
              active:scale-95
            `}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Floating decorative shapes
 */
function FloatingShape({ index, discipline }: { index: number; discipline: DisciplineSlug }) {
  const shapes = ["star", "circle", "sparkle"];
  const shape = shapes[index % shapes.length];

  const colors: Record<DisciplineSlug, string[]> = {
    math: ["text-luna-light", "text-magic-300", "text-sunshine-300"],
    spelling: ["text-petal-light", "text-meadow-300", "text-fairy-200"],
    patterns: ["text-prism-red", "text-prism-yellow", "text-prism-blue"],
    typing: ["text-whiskers-light", "text-sky-300", "text-sunshine-200"],
    programming: ["text-pixel-light", "text-encourage-light", "text-sunshine-300"],
  };

  const color = colors[discipline][index % 3];

  const baseStyle: React.CSSProperties = {
    left: `${10 + (index * 12) % 80}%`,
    top: `${10 + (index * 17) % 80}%`,
    animationDelay: `${index * 0.3}s`,
    animationDuration: `${3 + (index % 3)}s`,
  };

  return (
    <div
      className={`absolute w-8 h-8 ${color} opacity-40 animate-float`}
      style={baseStyle}
    >
      {shape === "star" && (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      )}
      {shape === "circle" && (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="12" r="10" />
        </svg>
      )}
      {shape === "sparkle" && (
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0L14.59 9.41L24 12L14.59 14.59L12 24L9.41 14.59L0 12L9.41 9.41L12 0Z" />
        </svg>
      )}
    </div>
  );
}

export default LevelCompleteSummary;
